import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  Box,
  Button,
  ButtonProps,
  IconButton,
  Modal,
  Stack,
  SxProps,
  Tab,
  Tabs,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  footerStyles,
  headerStyles,
  imageStyles,
  modalStyles,
  stageStyles,
  tabIndicatorStyle,
  tabsStyles,
  tabStyles,
  titleIconStyles,
  triggerButtonStyles,
} from './style';

type CardSide = 'front' | 'back';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const TOGGLE_ZOOM = 2.5;

const clampZoom = (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

type Props = {
  front?: string | null;
  back?: string | null;
  buttonProps?: ButtonProps;
};

export const InsuranceCardViewer = ({ front, back, buttonProps }: Props) => {
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState<CardSide>(front ? 'front' : 'back');
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const stageRef = useRef<HTMLDivElement>(null);

  const isZoomed = zoom > MIN_ZOOM;

  const sides = useMemo<{ key: CardSide; label: string; url: string }[]>(() => {
    const candidates: { key: CardSide; label: string; url?: string | null }[] = [
      { key: 'front', label: 'Front', url: front },
      { key: 'back', label: 'Back', url: back },
    ];
    return candidates.filter(
      (s): s is { key: CardSide; label: string; url: string } => !!s.url,
    );
  }, [front, back]);

  const hasCard = sides.length > 0;
  const activeUrl = sides.find((s) => s.key === side)?.url ?? sides[0]?.url;

  const handleOpen = () => {
    // Reset to the first available side each time the viewer is opened.
    setSide(sides[0]?.key ?? 'front');
    setZoom(MIN_ZOOM);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleTabChange = (_: React.SyntheticEvent, value: CardSide) => {
    setSide(value);
    setZoom(MIN_ZOOM);
  };

  const toggleZoom = () => setZoom((prev) => (prev > MIN_ZOOM ? MIN_ZOOM : TOGGLE_ZOOM));

  useEffect(() => {
    const stage = stageRef.current;
    if (!open || !stage) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      if (!event.ctrlKey) {
        return;
      }
      event.preventDefault();
      setZoom((prev) => clampZoom(prev - event.deltaY * 0.01));
    };

    stage.addEventListener('wheel', handleWheel, { passive: false });
    return () => stage.removeEventListener('wheel', handleWheel);
  }, [open]);

  return (
    <>
      <Button
        variant='contained'
        fullWidth
        startIcon={<CreditCardIcon />}
        {...buttonProps}
        disabled={!hasCard || buttonProps?.disabled}
        sx={[triggerButtonStyles, buttonProps?.sx] as SxProps<Theme>}
        onClick={handleOpen}>
        {buttonProps?.children ?? (hasCard ? 'Insurance card' : 'No insurance card')}
      </Button>

      <Modal open={open} onClose={handleClose} BackdropProps={{ timeout: 500 }}>
        <Stack sx={modalStyles}>
          {/* Header */}
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            sx={headerStyles}>
            <Stack direction='row' alignItems='center' spacing={1}>
              <CreditCardIcon fontSize='small' sx={titleIconStyles} />
              <Typography variant='subtitle1' fontWeight={600}>
                Insurance card
              </Typography>
            </Stack>
            <Stack direction='row' alignItems='center' spacing={0.5}>
              <Tooltip title={isZoomed ? 'Fit to screen' : 'Zoom in'}>
                <span>
                  <IconButton size='small' onClick={toggleZoom} disabled={!activeUrl}>
                    {isZoomed ? (
                      <ZoomOutIcon fontSize='small' />
                    ) : (
                      <ZoomInIcon fontSize='small' />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Open in new tab'>
                <span>
                  <IconButton
                    size='small'
                    component='a'
                    href={activeUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    disabled={!activeUrl}>
                    <OpenInNewIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Close'>
                <IconButton size='small' onClick={handleClose}>
                  <CloseIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Tabs (only shown when both sides are available) */}
          {sides.length > 1 && (
            <Tabs
              value={side}
              onChange={handleTabChange}
              sx={tabsStyles}
              TabIndicatorProps={{ style: tabIndicatorStyle }}>
              {sides.map((s) => (
                <Tab key={s.key} value={s.key} label={s.label} sx={tabStyles} />
              ))}
            </Tabs>
          )}

          {/* Image stage */}
          <Box
            ref={stageRef}
            onClick={activeUrl ? toggleZoom : undefined}
            sx={stageStyles(!!activeUrl, isZoomed)}>
            {activeUrl && (
              <Box
                component='img'
                alt={`Insurance card ${side}`}
                src={activeUrl}
                sx={imageStyles(isZoomed, zoom)}
              />
            )}
          </Box>

          {/* Footer hint */}
          <Box sx={footerStyles}>
            <Typography variant='caption' color='text.secondary'>
              Click the image to {isZoomed ? 'fit it to the screen' : 'zoom in and read details'}
              {' — or pinch on the trackpad to zoom.'}
            </Typography>
          </Box>
        </Stack>
      </Modal>
    </>
  );
};
